import { useCallback } from 'react';
import { Card, EmptyState, PageHeader, Spinner } from '../components/ui';
import { SearchInput } from '../components/SearchInput';
import { Pagination } from '../components/Pagination';
import { AuditAPI } from '../services/endpoints/admin';
import { usePagedQuery } from '../hooks/usePagedQuery';
import type { AuditEntry, Page } from '../types/api';
import { formatDateTime } from '../utils/format';
import { NotImplementedBanner } from './UsersPage';
import './common.css';
import './AuditPage.css';

export function AuditPage() {
  const fetcher = useCallback(async ({ page, q }: { page: number; q: string }): Promise<Page<AuditEntry>> => {
    return AuditAPI.list(page, 50, q || undefined);
  }, []);
  const q = usePagedQuery<AuditEntry, Record<string, never>>({ fetcher, initialFilters: {}, pageSize: 50 });

  return (
    <div>
      <PageHeader
        eyebrow="10 — AUDIT"
        title={<>The <em>ledger</em>.</>}
        lede="Every admin action, in order, with actor and reason. Nothing is deleted from this log."
        actions={<SearchInput value={q.q} onChange={q.setQ} placeholder="Filter by actor email…" />}
      />

      {q.error && <NotImplementedBanner endpoint="/api/admin/audit" />}

      {q.loading && !q.data.length ? <Card><Spinner /> Loading…</Card> :
       q.data.length === 0 ? <Card><EmptyState title="No audit entries" hint="As admin actions happen they'll appear here." /></Card> :
        <Card padded={false}>
          <table className="nb-table nb-audit__table">
            <thead>
              <tr>
                <th className="nb-audit__col-when">When</th>
                <th>Actor</th>
                <th className="nb-audit__col-action">Action</th>
                <th>Resource</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map(e => (
                <tr key={e.id}>
                  <td className="nb-table__mono">{formatDateTime(e.timestamp)}</td>
                  <td>{e.actor}</td>
                  <td><span className="nb-audit__action">{e.action}</span></td>
                  <td className="nb-table__mono">{e.resource}{e.resourceId ? ` · ${e.resourceId.slice(0, 12)}` : ''}</td>
                  <td className="nb-audit__reason">{e.reason || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pagination page={q.page} pageSize={q.pageSize} total={q.totalCount} onPage={q.setPage} />
        </Card>
      }
    </div>
  );
}
